import {
  GetSnowflakeJobItem,
  GetSnowflakeJobsParams,
  SnowflakeJobFilters,
  WorkflowType,
} from "@/pages/query/types";
import { FC, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppState } from "@/modules/app/useAppContext";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useQuery } from "@tanstack/react-query";
import { getSnowflakeJobs } from "@/helpers/apis";
import { FilterApplicableFor } from "@/components/tags/types";
import { Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { GET_DBT_JOBS_LIST } from "@/pages/query/constants";
import { parseSortDirection } from "@/components/tables/types";
import { useDbtJobsTableHeaders } from "./useDbtJobsTableHeaders";

interface DbtJobsTableProps {
  filters?: SnowflakeJobFilters;
}

export const DbtJobsTable: FC<DbtJobsTableProps> = ({ filters }) => {
  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    setTotalPages,
    totalItems,
    setTotalItems,
  } = useTablePagination();
  const { getFilterValue } = useDatatableContext();
  const [searchParams] = useSearchParams();

  // Sort state from URL params
  const sortAttribute = searchParams.get("sortAttribute") || "query_cost";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");
  const { currency, currencySymbol, enableDbtJobsListing } = useAppState();

  const costFilter = useMemo(() => {
    return getFilterValue<string>("query_cost") || "";
  }, [getFilterValue]);

  const executionTimeFilter = useMemo(() => {
    return getFilterValue<string>("executionTime") || "";
  }, [getFilterValue]);

  const teamsFilter = useMemo(() => {
    return getFilterValue<string[]>("teams") || [];
  }, [getFilterValue]);

  const appsFilter = useMemo(() => {
    return getFilterValue<string[]>("apps") || [];
  }, [getFilterValue]);

  const workflowFilter = useMemo(() => {
    return getFilterValue<string[]>("workflows") || [];
  }, [getFilterValue]);

  const warehouseFilter = useMemo(() => {
    return getFilterValue<string[]>("warehouses") || [];
  }, [getFilterValue]);

  const integrationNameFilter = useMemo(() => {
    return getFilterValue<string[]>("integration_names") || [];
  }, [getFilterValue]);

  const integrationEnvironmentFilter = useMemo(() => {
    return getFilterValue<string[]>("integration_environments") || [];
  }, [getFilterValue]);

  const executionTimeFilterMode = useMemo(
    () => getFilterValue<string>("executionTimeFilterMode") || "gt",
    [getFilterValue]
  );

  const costFilterMode = useMemo(
    () => getFilterValue<string>("costFilterMode") || "gt",
    [getFilterValue]
  );

  const { startDate, endDate } = useDateFromSearchParams();

  // Query parameters
  const getFilterParams = () => {
    const params: Partial<GetSnowflakeJobsParams> = {
      page: page + 1,
      size: pageSize,
      sortAttribute,
      sortOrder,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      workflows: workflowFilter,
      warehouses: warehouseFilter,
      teams: teamsFilter,
      apps: appsFilter,
      integration_names: integrationNameFilter,
      integration_environments: integrationEnvironmentFilter,
      workflow_type: WorkflowType.DBT,
    };

    if (costFilter) {
      params.costFilter = costFilter;
      params.costFilterMode = costFilterMode === "lt" ? "lt" : "gt";
    }

    if (executionTimeFilter) {
      params.executionTimeFilter = parseFloat(executionTimeFilter) * 1000;
      params.executionTimeFilterMode = executionTimeFilterMode === "lt" ? "lt" : "gt";
    }

    return params;
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      GET_DBT_JOBS_LIST,
      page,
      pageSize,
      sortAttribute,
      sortOrder,
      costFilter,
      costFilterMode,
      ...workflowFilter,
      ...warehouseFilter,
      ...teamsFilter,
      ...appsFilter,
      ...integrationNameFilter,
      ...integrationEnvironmentFilter,
      executionTimeFilter,
      executionTimeFilterMode,
      startDate,
      endDate,
    ],
    queryFn: () => getSnowflakeJobs(getFilterParams()),
  });

  useEffect(() => {
    if (data) {
      setTotalPages(data.pages);
      setTotalItems(data.total);
    }
  }, [data, setTotalPages, setTotalItems]);

  const tableHeaders = useDbtJobsTableHeaders({
    currency,
    currencySymbol,
    filters,
    defaultTab: enableDbtJobsListing ? "jobs" : "models",
  });

  return (
    <Stack direction="column">
      <Stack align="center">
        <Typography variant={"section-heading"}>Projects List</Typography>
      </Stack>
      <DataTable
        header={tableHeaders}
        data={data ?? { items: [] }}
        idKey={(item: GetSnowflakeJobItem) => item.workflow_id}
        isLoading={isLoading}
        applicableFor={FilterApplicableFor.DBT_JOBS}
        portals={{
          filter: "dbt-filter-container",
        }}
      />

      <Paginate
        itemCount={totalItems}
        numPages={totalPages}
        page={page}
        pageSize={pageSize}
        onPageClick={setPage}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
        showPageSize
      />
    </Stack>
  );
};
